import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ManagetNav from '../../components/ManagerNav/ManagerNav';
import { ContextTicketDto, TicketStatusNameType } from '../HomeView/HomeView';
import './ManagerTicketsView.scss';
import { getStatusColor } from '../../utils/GetStatusColor';

/**
 * Legacy case search. Superseded by the Open / Closed case lists (/cases/open, /cases/closed) and
 * removed from the menu, but kept routable so existing links and bookmarks still work.
 *
 * The "Ticket Status: Active / Inactive" dropdown that used to sit here has been removed rather
 * than repaired. It filtered on `Ticket_Active`, a flag from the previous system that is set to
 * true when a case is created and is never set to false anywhere in the codebase — closing a case
 * writes a `ticket_status` row and does not touch it. So "Active" returned every case and
 * "Inactive" returned none, while looking exactly like an open/closed filter. Open vs closed is a
 * real question answered by /cases/:state.
 */
interface SearchFilters {
  userFirstName: string;
  userLastName: string;
  userEmail: string;
}

const initialFilters: SearchFilters = {
  userFirstName: '',
  userLastName: '',
  userEmail: '',
};

const ManagerTicketsView: React.FC = () => {
  const navigate = useNavigate();
  
  const [tickets, setTickets] = useState<ContextTicketDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const RESULTS_PER_PAGE = 20;

  // Remove debounced search state

  const fetchTickets = useCallback(async (page = 0, searchFilters = filters, appendResults = false) => {
    if (!appendResults) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const params = new URLSearchParams();

      // The endpoint reads `from`/`size`, not `page`/`limit`. It was being sent the latter, so the
      // unrecognised params were ignored and every "Load More" re-fetched page 1 and appended a
      // duplicate copy of the first 20 results.
      params.append('from', (page * RESULTS_PER_PAGE).toString());
      params.append('size', RESULTS_PER_PAGE.toString());

      // Add filters if they have values
      if (searchFilters.userFirstName.trim()) {
        params.append('User_FirstName', searchFilters.userFirstName.trim());
      }
      if (searchFilters.userLastName.trim()) {
        params.append('User_LastName', searchFilters.userLastName.trim());
      }
      if (searchFilters.userEmail.trim()) {
        params.append('User_Email', searchFilters.userEmail.trim());
      }

      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/tickets?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch tickets: ${response.status} ${response.statusText}`);
      }

      const data: ContextTicketDto[] = await response.json();
      
      if (appendResults) {
        setTickets(prevTickets => [...prevTickets, ...data]);
      } else {
        setTickets(data);
      }
      
      // Check if there are more results (if we got exactly RESULTS_PER_PAGE, there might be more)
      setHasMoreData(data.length === RESULTS_PER_PAGE);
      setCurrentPage(page);
      setHasSearched(true);
      
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [RESULTS_PER_PAGE]);

  // Initial load
  useEffect(() => {
    fetchTickets(0);
  }, []);

  // Handle input changes without debouncing
  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setFilters({ ...filters, [field]: value });
  };

  // Handle search button click
  const handleSearch = () => {
    setCurrentPage(0);
    setHasMoreData(false);
    fetchTickets(0, filters, false);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    fetchTickets(nextPage, filters, true);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(0);
    setHasMoreData(false);
    setHasSearched(false);
    fetchTickets(0, initialFilters, false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <>
      <ManagetNav />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Manage Tickets
        </Typography>

        {/* Search Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Filters
          </Typography>
          <Button
            variant="outlined"
            sx={{ mb: 2 }}
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            size="small"
          >
            Clear Filters
          </Button>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField
              label="User First Name"
              value={filters.userFirstName}
              onChange={(e) => handleFilterChange('userFirstName', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
            />
            
            <TextField
              label="User Last Name"
              value={filters.userLastName}
              onChange={(e) => handleFilterChange('userLastName', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
            />
            
            <TextField
              label="User Email"
              value={filters.userEmail}
              onChange={(e) => handleFilterChange('userEmail', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
            />
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Looking for open or closed cases? Use <strong>Open Cases</strong> or{' '}
            <strong>Closed Cases</strong> in the menu. This page searches every case regardless of
            stage.
          </Alert>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              size="small"
              sx={{ mr: 1 }}
            >
              Search
            </Button>
          </Box>
        </Paper>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Results */}
        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {hasSearched && (
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" color="textSecondary">
                    {tickets.length === 0 ? 'No tickets found' : `${tickets.length} ticket${tickets.length === 1 ? '' : 's'} found`}
                  </Typography>
                </Box>
              )}
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket Number</TableCell>
                      <TableCell>Client Name</TableCell>
                      <TableCell>Citation</TableCell>
                      <TableCell>Representation</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Court Date</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.length === 0 && hasSearched ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No tickets found matching your search criteria.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : tickets.length === 0 && !hasSearched ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="textSecondary">
                            Use the search filters above to find tickets.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tickets.map((contextTicket) => (
                        <TableRow
                          key={contextTicket.ticket.Ticket_guid}
                          hover
                          component={Link}
                          to={`/ticket?Ticket_guid=${contextTicket.ticket.Ticket_guid}`}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                            cursor: 'pointer',
                            textDecoration: 'none',
                            color: 'inherit',
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 'medium' }}
                            >
                              {contextTicket.ticket.Ticket_Number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {contextTicket?.user && 
                              `${contextTicket.user.User_FirstName} ${contextTicket.user.User_LastName}`
                            }
                          </TableCell>
                          <TableCell>{contextTicket.ticket.Ticket_Citation}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {contextTicket.ticket.Ticket_Representation || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {contextTicket.ticketStatus && (
                              <Chip
                                label={contextTicket.ticketStatus.TicketStatus_Name}
                                color={getStatusColor(contextTicket.ticketStatus.TicketStatus_Name)}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {new Date(contextTicket.ticket.Ticket_CourtDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {new Date(contextTicket.ticket.Ticket_CreatedAt).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Load More Button */}
              {hasMoreData && tickets.length > 0 && (
                <Box sx={{ p: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={20} /> : undefined}
                  >
                    {loadingMore ? 'Loading...' : `Load More (Showing ${tickets.length} results)`}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default ManagerTicketsView;
